import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Check, Trash2, Loader2, Lock, ArrowLeft } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value }) => (
    <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
            <Star key={n} size={16} className={n <= value ? "fill-sky-400 text-sky-400" : "text-slate-700"} />
        ))}
    </div>
);

const inputCls =
    "w-full bg-[#0F172A]/60 border border-slate-700 text-slate-100 px-4 py-3 text-sm placeholder:text-slate-600 focus:outline-none focus:border-sky-400 transition-colors duration-300";

export default function Admin() {
    const [token, setToken] = useState(() => sessionStorage.getItem("admin_token") || "");
    const [pin, setPin] = useState("");
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(false);

    const authHeader = { headers: { Authorization: `Bearer ${token}` } };

    const load = async () => {
        try {
            const res = await axios.get(`${API}/admin/reviews`, authHeader);
            setReviews(res.data.reviews || []);
        } catch {
            sessionStorage.removeItem("admin_token");
            setToken("");
        }
    };

    useEffect(() => {
        if (token) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const login = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await axios.post(`${API}/admin/login`, { pin });
            sessionStorage.setItem("admin_token", res.data.token);
            setToken(res.data.token);
        } catch {
            toast.error("PIN errato. Riprova.");
        } finally {
            setLoading(false);
        }
    };

    const approve = async (id) => {
        await axios.post(`${API}/admin/reviews/${id}/approve`, {}, authHeader);
        toast.success("Recensione pubblicata.");
        load();
    };

    const remove = async (id) => {
        await axios.delete(`${API}/admin/reviews/${id}`, authHeader);
        toast.success("Recensione eliminata.");
        load();
    };

    return (
        <div className="min-h-screen bg-[#020617] text-slate-100">
            <div className="max-w-3xl mx-auto px-6 py-16">
                <div className="flex items-center justify-between">
                    <a href="/" data-testid="admin-back-home" className="flex items-center gap-3 font-serif text-2xl hover:text-sky-400 transition-colors duration-300">
                        <LogoMark size={36} /> Ripetizioni²
                    </a>
                    <a href="/" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-500 hover:text-sky-400 transition-colors duration-300">
                        <ArrowLeft size={14} /> Torna al sito
                    </a>
                </div>

                {!token ? (
                    <form data-testid="admin-login-form" onSubmit={login} className="mt-16 border border-slate-800 bg-[#0F172A]/40 p-8 max-w-md">
                        <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-slate-500">
                            <Lock size={14} /> Area gestori
                        </p>
                        <h1 className="mt-4 font-serif text-3xl">Solo per Tommaso e Alessandro</h1>
                        <input
                            data-testid="admin-pin-input"
                            type="password"
                            required
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                            placeholder="Inserisci il PIN"
                            className={`${inputCls} mt-6`}
                        />
                        <button
                            data-testid="admin-login-button"
                            type="submit"
                            disabled={loading}
                            className="mt-4 w-full inline-flex items-center justify-center gap-3 bg-slate-50 text-[#020617] font-semibold px-8 py-4 hover:bg-sky-400 disabled:opacity-60 transition-colors duration-300"
                        >
                            {loading ? <Loader2 size={18} className="animate-spin" /> : null}
                            Entra
                        </button>
                    </form>
                ) : (
                    <div className="mt-16" data-testid="admin-reviews-panel">
                        <h1 className="font-serif text-4xl">Moderazione recensioni</h1>
                        <p className="mt-3 text-slate-400 text-sm">
                            Le recensioni in attesa non sono visibili sul sito finché non le approvi.
                        </p>
                        <div className="mt-10 space-y-5">
                            {reviews.length === 0 && (
                                <p className="border border-dashed border-slate-700 p-10 text-center text-slate-400">
                                    Nessuna recensione al momento.
                                </p>
                            )}
                            {reviews.map((r, i) => (
                                <article key={r.id} data-testid={`admin-review-${i}`} className="border border-slate-800 bg-[#0F172A]/40 p-6">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div className="flex items-center gap-4">
                                            <Stars value={r.rating} />
                                            <span className={`font-mono text-xs uppercase tracking-[0.2em] ${r.approved ? "text-emerald-400" : "text-amber-400"}`}>
                                                {r.approved ? "Pubblicata" : "In attesa"}
                                            </span>
                                        </div>
                                        <div className="flex gap-2">
                                            {!r.approved && (
                                                <button
                                                    data-testid={`admin-approve-${i}`}
                                                    onClick={() => approve(r.id)}
                                                    className="inline-flex items-center gap-2 bg-slate-50 text-[#020617] font-semibold text-sm px-4 py-2 hover:bg-sky-400 transition-colors duration-300"
                                                >
                                                    <Check size={15} /> Approva
                                                </button>
                                            )}
                                            <button
                                                data-testid={`admin-delete-${i}`}
                                                onClick={() => remove(r.id)}
                                                className="inline-flex items-center gap-2 border border-slate-600 text-slate-300 font-semibold text-sm px-4 py-2 hover:border-red-400 hover:text-red-400 transition-colors duration-300"
                                            >
                                                <Trash2 size={15} /> Elimina
                                            </button>
                                        </div>
                                    </div>
                                    <p className="mt-4 text-slate-300 leading-relaxed">"{r.text}"</p>
                                    <p className="mt-3 font-mono text-xs text-slate-500">
                                        {r.name} · {r.level} · {new Date(r.created_at).toLocaleDateString("it-IT")}
                                    </p>
                                </article>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
